import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Search,
  Download,
  ChevronDown,
  ChevronUp,
  Eye,
  Loader2,
  ExternalLink,
  MapPin,
  Phone,
  Mail,
  User,
  FileText,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import operatorService from '../services/operatorService';
import * as XLSX from 'xlsx';

// ── helpers ───────────────────────────────────────────────────────────────────

function firstLicense(op) {
  return op.licenses?.[0] ?? null;
}

function getStatusBadge(status) {
  const s = status?.toLowerCase() || '';
  let cls = 'bg-gray-100 text-gray-700';
  if (s === 'active') cls = 'bg-green-100 text-green-800';
  else if (s === 'pending') cls = 'bg-yellow-100 text-yellow-800';
  else if (['inactive', 'expired', 'revoked', 'cancelled'].includes(s)) cls = 'bg-red-100 text-red-800';
  else if (s === 'suspended') cls = 'bg-orange-100 text-orange-800';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize ${cls}`}>
      {status || '—'}
    </span>
  );
}

function formatDate(d) {
  if (!d) return 'N/A';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function InfoRow({ label, value }) {
  return (
    <div>
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="font-medium">{value || 'N/A'}</p>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function OperatorsPage() {
  const [operators, setOperators] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'businessName', direction: 'asc' });
  const [isLoading, setIsLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  useEffect(() => { fetchOperators(); }, []);

  const fetchOperators = async () => {
    try {
      setIsLoading(true);
      const data = await operatorService.getOperators();
      const list = Array.isArray(data) ? data : [];
      setOperators(list);
      setFiltered(list);
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to fetch operators');
      setOperators([]);
      setFiltered([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter + sort
  useEffect(() => {
    let list = [...operators];
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      list = list.filter(op =>
        op.businessName?.toLowerCase().includes(t) ||
        op.corporateName?.toLowerCase().includes(t) ||
        op.dbaName?.toLowerCase().includes(t) ||
        op.city?.toLowerCase().includes(t) ||
        op.county?.toLowerCase().includes(t) ||
        op.licenses?.some(l =>
          l.licenseNumber?.toLowerCase().includes(t) ||
          l.state?.toLowerCase().includes(t) ||
          l.licenseType?.toLowerCase().includes(t)
        )
      );
    }
    list.sort((a, b) => {
      let aVal = '', bVal = '';
      if (sortConfig.key === 'licenseNumber') {
        aVal = firstLicense(a)?.licenseNumber ?? '';
        bVal = firstLicense(b)?.licenseNumber ?? '';
      } else if (sortConfig.key === 'licenseType') {
        aVal = firstLicense(a)?.licenseType ?? '';
        bVal = firstLicense(b)?.licenseType ?? '';
      } else if (sortConfig.key === 'state') {
        aVal = firstLicense(a)?.state ?? '';
        bVal = firstLicense(b)?.state ?? '';
      } else {
        aVal = a[sortConfig.key] ?? '';
        bVal = b[sortConfig.key] ?? '';
      }
      return sortConfig.direction === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
    setFiltered(list);
  }, [searchTerm, sortConfig, operators]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === 'asc'
      ? <ChevronUp className="w-3 h-3 inline ml-1" />
      : <ChevronDown className="w-3 h-3 inline ml-1" />;
  };

  const handleExportExcel = () => {
    try {
      const rows = filtered.flatMap(op => {
        const base = {
          'Business Name': op.businessName,
          'Corporate Name': op.corporateName,
          'DBA Name': op.dbaName,
          'Phone': op.phone,
          'Email': op.email,
          'Address': [op.streetAddress, op.city, op.zipCode].filter(Boolean).join(', '),
          'County': op.county,
          'Website': op.website,
        };
        if (!op.licenses?.length) return [{ ...base, 'License Number': '', 'License Type': '', 'License Status': '', 'State': '' }];
        return op.licenses.map(l => ({
          ...base,
          'License Number': l.licenseNumber,
          'License Type': l.licenseType,
          'License Status': l.licenseStatus,
          'State': l.state,
          'Issue Date': l.issueDate ? new Date(l.issueDate).toLocaleDateString() : '',
          'Expiration Date': l.expirationDate ? new Date(l.expirationDate).toLocaleDateString() : '',
        }));
      });
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Operators');
      XLSX.writeFile(wb, `operators_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Operators exported successfully');
    } catch {
      toast.error('Failed to export data');
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Loading operators...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <CardTitle>My Operators</CardTitle>
                <CardDescription>
                  Operators in your subscription quota ({filtered.length} of {operators.length})
                </CardDescription>
              </div>
              <Button onClick={handleExportExcel}>
                <Download className="w-4 h-4 mr-2" />Export to Excel
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Search */}
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by business name, corporate name, license number, state, city..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-3 font-medium text-muted-foreground cursor-pointer whitespace-nowrap"
                      onClick={() => handleSort('businessName')}>
                      Business Name <SortIcon columnKey="businessName" />
                    </th>
                    <th className="text-left py-3 px-3 font-medium text-muted-foreground whitespace-nowrap">
                      Corporate Name
                    </th>
                    <th className="text-left py-3 px-3 font-medium text-muted-foreground cursor-pointer whitespace-nowrap"
                      onClick={() => handleSort('licenseNumber')}>
                      License # <SortIcon columnKey="licenseNumber" />
                    </th>
                    <th className="text-left py-3 px-3 font-medium text-muted-foreground cursor-pointer whitespace-nowrap"
                      onClick={() => handleSort('licenseType')}>
                      License Type <SortIcon columnKey="licenseType" />
                    </th>
                    <th className="text-left py-3 px-3 font-medium text-muted-foreground whitespace-nowrap">Status</th>
                    <th className="text-left py-3 px-3 font-medium text-muted-foreground cursor-pointer whitespace-nowrap"
                      onClick={() => handleSort('state')}>
                      State <SortIcon columnKey="state" />
                    </th>
                    <th className="text-left py-3 px-3 font-medium text-muted-foreground cursor-pointer whitespace-nowrap"
                      onClick={() => handleSort('city')}>
                      Location <SortIcon columnKey="city" />
                    </th>
                    <th className="text-left py-3 px-3 font-medium text-muted-foreground whitespace-nowrap">Contact</th>
                    <th className="text-left py-3 px-3 font-medium text-muted-foreground whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(op => {
                    const lic = firstLicense(op);
                    return (
                      <tr key={op.operatorId} className="border-b hover:bg-muted/40 transition">
                        <td className="py-3 px-3">
                          <div className="font-medium">{op.businessName}</div>
                          {op.dbaName && <div className="text-xs text-muted-foreground">DBA: {op.dbaName}</div>}
                        </td>
                        <td className="py-3 px-3 text-muted-foreground text-xs">
                          {op.corporateName || '—'}
                        </td>
                        <td className="py-3 px-3">
                          <div>{lic?.licenseNumber || '—'}</div>
                          {op.licenses?.length > 1 && (
                            <div className="text-xs text-muted-foreground">+{op.licenses.length - 1} more</div>
                          )}
                        </td>
                        <td className="py-3 px-3 text-muted-foreground">{lic?.licenseType || '—'}</td>
                        <td className="py-3 px-3">{getStatusBadge(lic?.licenseStatus)}</td>
                        <td className="py-3 px-3">
                          {lic?.state
                            ? <>
                                <Badge variant="secondary">{lic.state}</Badge>
                                {(op.stateCount ?? op.licenses?.length ?? 0) > 1 && (
                                  <div className="text-xs text-muted-foreground mt-0.5">
                                    +{(op.stateCount ?? op.licenses.length) - 1} more
                                  </div>
                                )}
                              </>
                            : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="py-3 px-3 text-muted-foreground">
                          {op.city && <div>{op.city}</div>}
                          {op.county && <div className="text-xs">{op.county} County</div>}
                        </td>
                        <td className="py-3 px-3 text-muted-foreground">
                          {op.phone && <div>{op.phone}</div>}
                          {op.email && <div className="text-xs truncate max-w-[140px]">{op.email}</div>}
                        </td>
                        <td className="py-3 px-3">
                          <Button variant="ghost" size="sm" onClick={() => { setSelected(op); setIsDetailOpen(true); }}>
                            <Eye className="w-4 h-4 mr-1" />View
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {filtered.length === 0 && (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No operators found matching your criteria.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Detail Dialog ─────────────────────────────────────────────────────── */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">{selected?.businessName}</DialogTitle>
            <DialogDescription>
              {selected?.corporateName && (
                <span className="font-medium text-foreground">{selected.corporateName} · </span>
              )}
              Operator ID: {selected?.operatorId}
            </DialogDescription>
          </DialogHeader>

          {selected && (
            <div className="space-y-6 mt-2">
              {/* Basic Info */}
              <section>
                <h3 className="font-semibold text-base mb-3 flex items-center gap-2">
                  <User className="w-4 h-4 text-primary" />Basic Information
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-muted/30 p-4 rounded-lg">
                  <InfoRow label="Business Name" value={selected.businessName} />
                  <InfoRow label="Corporate Name" value={selected.corporateName} />
                  <InfoRow label="DBA Name" value={selected.dbaName} />
                  <InfoRow label="States" value={(selected.stateCount ?? [...new Set((selected.licenses || []).map(l => l.state).filter(Boolean))].length) || null} />
                  <InfoRow label="Licenses" value={selected.licenseCount ?? selected.licenses?.length ?? null} />
                  <InfoRow label="Energy Supplier" value={selected.energySupplier} />
                  <InfoRow label="Certificate Body" value={selected.certificateBody} />
                  {selected.notes && (
                    <div className="col-span-2 md:col-span-3">
                      <InfoRow label="Notes" value={selected.notes} />
                    </div>
                  )}
                </div>
              </section>

              {/* Contact Info */}
              <section>
                <h3 className="font-semibold text-base mb-3 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-primary" />Contact Information
                </h3>
                <div className="grid grid-cols-2 gap-4 bg-muted/30 p-4 rounded-lg">
                  <InfoRow label="Phone" value={selected.phone} />
                  <InfoRow label="Email" value={selected.email} />
                  {selected.website && (
                    <div className="col-span-2">
                      <p className="text-sm text-muted-foreground">Website</p>
                      <a href={selected.website} target="_blank" rel="noopener noreferrer"
                        className="font-medium text-primary hover:underline flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" />{selected.website}
                      </a>
                    </div>
                  )}
                </div>
              </section>

              {/* Location */}
              <section>
                <h3 className="font-semibold text-base mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-primary" />Location
                </h3>
                <div className="bg-muted/30 p-4 rounded-lg space-y-3">
                  <InfoRow label="Street Address" value={selected.streetAddress} />
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <InfoRow label="City" value={selected.city} />
                    <InfoRow label="County" value={selected.county} />
                    <InfoRow label="Zip Code" value={selected.zipCode} />
                    {selected.latitude && selected.longitude && (
                      <InfoRow label="Coordinates" value={`${selected.latitude}, ${selected.longitude}`} />
                    )}
                  </div>
                </div>
              </section>

              {/* Licenses */}
              {selected.licenses?.length > 0 && (
                <section>
                  <h3 className="font-semibold text-base mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-primary" />Licenses ({selected.licenses.length})
                  </h3>
                  <div className="space-y-3">
                    {selected.licenses.map((l, i) => (
                      <div key={l.operatorLicenseId ?? i} className="bg-muted/30 p-4 rounded-lg">
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                          <span className="font-semibold">{l.licenseNumber || `License ${i + 1}`}</span>
                          {l.state && <Badge variant="secondary">{l.state}</Badge>}
                          {getStatusBadge(l.licenseStatus)}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <InfoRow label="License Type" value={l.licenseType} />
                          <InfoRow label="Designation" value={l.licenseDesignation} />
                          <InfoRow label="Category" value={l.category} />
                          <InfoRow label="Vertical" value={l.vertical} />
                          <InfoRow label="Activity" value={l.activity} />
                          <InfoRow label="Term" value={l.licenseTerm} />
                          <InfoRow label="Issue Date" value={formatDate(l.issueDate)} />
                          <InfoRow label="Expiration Date" value={formatDate(l.expirationDate)} />
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Contacts */}
              {selected.contacts?.length > 0 && (
                <section>
                  <h3 className="font-semibold text-base mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4 text-primary" />Contacts ({selected.contacts.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {selected.contacts.map((c, i) => (
                      <div key={c.operatorContactId ?? i} className="bg-muted/30 p-4 rounded-lg">
                        <p className="font-medium">
                          {[c.firstName, c.lastName].filter(Boolean).join(' ') || `Contact ${i + 1}`}
                        </p>
                        {c.contactType && <p className="text-xs text-muted-foreground mb-2">{c.contactType}</p>}
                        {c.phone && (
                          <p className="text-sm flex items-center gap-1 text-muted-foreground">
                            <Phone className="w-3 h-3" />{c.phone}
                          </p>
                        )}
                        {c.email && (
                          <p className="text-sm flex items-center gap-1 text-muted-foreground">
                            <Mail className="w-3 h-3" />{c.email}
                          </p>
                        )}
                        {c.address && <p className="text-xs text-muted-foreground mt-1">{c.address}</p>}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Social */}
              {(selected.facebook || selected.instagram || selected.twitter || selected.linkedin) && (
                <section>
                  <h3 className="font-semibold text-base mb-3 flex items-center gap-2">
                    <ExternalLink className="w-4 h-4 text-primary" />Online Presence
                  </h3>
                  <div className="grid grid-cols-2 gap-3 bg-muted/30 p-4 rounded-lg">
                    {[['Facebook', selected.facebook], ['Instagram', selected.instagram],
                      ['Twitter / X', selected.twitter], ['LinkedIn', selected.linkedin]]
                      .filter(([, url]) => url)
                      .map(([label, url]) => (
                        <a key={label} href={url} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1 text-primary hover:underline text-sm">
                          <ExternalLink className="w-3 h-3" />{label}
                        </a>
                      ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
